import type { Restroom } from "../types";
import { formatDistance, distanceMeters } from "../lib/geo";
import { localePath, wcPath } from "../lib/routes";
import { useLocale, useT } from "../i18n";
import StreetViewPreview from "./StreetViewPreview";

interface Props {
  restroom: Restroom;
  userPos: { lat: number; lng: number } | null;
  copied: boolean;
  onShare: (r: Restroom) => void;
  onClose: () => void;
}

function directionsUrl(r: Restroom): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${r.lat},${r.lng}`;
}

/**
 * Detail vybraného WC. Na mobilu vyjede odspodu jako bottom sheet,
 * na širších displejích plave jako karta vlevo dole.
 */
export default function RestroomDetail({ restroom, userPos, copied, onShare, onClose }: Props) {
  const t = useT();
  const { locale } = useLocale();
  return (
    <div className="sheet" role="dialog" aria-label={t("detail.dialogLabel")}>
      <button className="sheet-close" onClick={onClose} aria-label={t("detail.close")}>
        ✕
      </button>
      <div className="sheet-handle" />

      <h2 className="sheet-title">{restroom.name ?? t("detail.defaultName")}</h2>

      <div className="sheet-tags">
        <span className={restroom.fee ? "tag" : "tag tag-ok"}>
          {restroom.fee ? t("detail.paid") : t("detail.free")}
        </span>
        {restroom.wheelchair && <span className="tag tag-ok">{t("detail.wheelchair")}</span>}
        {restroom.changingTable && <span className="tag tag-ok">{t("detail.changingTable")}</span>}
      </div>

      {(restroom.openingHours || restroom.note || userPos) && (
        <ul className="sheet-meta">
          {restroom.openingHours && <li>🕒 {restroom.openingHours}</li>}
          {restroom.note && <li>📝 {restroom.note}</li>}
          {userPos && (
            <li>{t("detail.distance", { d: formatDistance(distanceMeters(userPos, restroom), locale) })}</li>
          )}
        </ul>
      )}

      <StreetViewPreview lat={restroom.lat} lng={restroom.lng} />

      <div className="sheet-actions">
        <a className="btn primary" href={directionsUrl(restroom)} target="_blank" rel="noreferrer">
          {t("detail.navigate")}
        </a>
        <button className="btn" onClick={() => onShare(restroom)}>
          {copied ? t("detail.shared") : t("detail.share")}
        </button>
        <a className="btn" href={localePath(locale, wcPath(restroom.id))}>
          {t("detail.openPage")}
        </a>
      </div>
    </div>
  );
}
